/**
 * The website's session client.
 *
 * ## What lives where (website-pages plan §3)
 *
 * - The **refresh token** is in an httpOnly cookie scoped to
 *   `/api/v1/web/session`. Nothing in this file can read it, by design; the
 *   browser attaches it to the session routes and to nothing else.
 * - The **access token** is held in this module's memory and nowhere else —
 *   not `localStorage`, not `sessionStorage`. A reload loses it, and the page
 *   gets a fresh one from the cookie with one `refresh` call.
 * - Every session call sends `X-JT-Web: 1`. The server refuses the session
 *   routes without it (and without a same-site `Origin`), which is the CSRF
 *   guard on top of `SameSite=Strict`.
 *
 * ## Refresh is single-flight, across tabs too
 *
 * The server rotates the refresh token on every use and treats a second use of
 * the same token as theft — the whole session ends. Two refreshes racing would
 * therefore sign the person out. Within a tab, one promise is shared; across
 * tabs, the Web Locks API serialises them, so the second tab sends the cookie
 * the first one just received.
 */

const BASE_URL: string = (import.meta.env?.VITE_API_BASE_URL as string | undefined) ?? '/api/v1';
const SESSION_BASE = `${BASE_URL}/web/session`;
const LOCK_NAME = 'jt-web-session-refresh';
/** Refresh a little before the access token runs out, not after. */
const EXPIRY_MARGIN_MS = 30_000;

export interface SessionAccount {
  readonly email: string;
  readonly personId: string;
}

export interface WebSession {
  readonly accessToken: string;
  readonly accessTokenExpiresAt: string;
  readonly sessionId: string;
  readonly account: SessionAccount;
}

/** An answer from the API that was not a success. Carries the server's code and plain message. */
export class SessionApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string | undefined,
    readonly details: Readonly<Record<string, unknown>> = {},
  ) {
    super(message);
    this.name = 'SessionApiError';
  }
}

let current: WebSession | undefined;
let inflight: Promise<WebSession | undefined> | undefined;
const listeners = new Set<(session: WebSession | undefined) => void>();

/** The session as this tab knows it. `undefined` is signed out (or not yet known). */
export function currentSession(): WebSession | undefined {
  return current;
}

/** Called whenever the session starts, refreshes or ends. */
export function onSessionChange(listener: (session: WebSession | undefined) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function setSession(next: WebSession | undefined): void {
  current = next;
  for (const listener of listeners) listener(next);
}

/** POSTs to a session route. The cookie travels because the request is same-origin. */
async function sessionPost<T>(path: string, body?: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${SESSION_BASE}/${path}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'x-jt-web': '1',
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch {
    throw new SessionApiError(
      'We could not reach Just Treated. Check your connection and try again.',
      0,
      undefined,
    );
  }

  const payload = (await response.json().catch(() => undefined)) as
    { data?: T; error?: { message?: string; code?: string; details?: Record<string, unknown> } } | undefined;

  if (!response.ok) {
    throw new SessionApiError(
      payload?.error?.message ?? 'Something went wrong. Try again.',
      response.status,
      payload?.error?.code,
      payload?.error?.details ?? {},
    );
  }

  return payload?.data as T;
}

/** Runs `work` holding the cross-tab refresh lock, where the browser has one. */
async function withRefreshLock<T>(work: () => Promise<T>): Promise<T> {
  const locks = (globalThis.navigator as Navigator | undefined)?.locks;
  if (locks === undefined || typeof locks.request !== 'function') return work();
  return locks.request(LOCK_NAME, work) as Promise<T>;
}

/**
 * A new access token from the cookie.
 *
 * Resolves `undefined` when the person is signed out (no cookie, or one the
 * server no longer honours). Any other failure — the network, a 5xx — throws,
 * because "we could not ask" is not the same as "you are signed out".
 */
export function refresh(): Promise<WebSession | undefined> {
  inflight ??= withRefreshLock(async () => {
    try {
      const session = await sessionPost<WebSession>('refresh');
      setSession(session);
      return session;
    } catch (error) {
      if (error instanceof SessionApiError && (error.status === 401 || error.status === 403)) {
        setSession(undefined);
        return undefined;
      }
      throw error;
    }
  }).finally(() => {
    inflight = undefined;
  });
  return inflight;
}

/** Asks for a sign-in code. Resolves with when it expires. */
export async function requestSignInCode(email: string): Promise<{ readonly expiresAt: string }> {
  return sessionPost<{ accepted: true; expiresAt: string }>('email-code', { email });
}

/** Signs in (or creates the account) with the emailed code. */
export async function signInWithCode(
  email: string,
  code: string,
): Promise<WebSession & { created: boolean }> {
  const session = await sessionPost<WebSession & { created: boolean }>('verify', { email, code });
  setSession(session);
  return session;
}

/** "Use a password instead". May throw AUTH_MFA_REQUIRED carrying an `mfaToken`. */
export async function signInWithPassword(email: string, password: string): Promise<WebSession> {
  const session = await sessionPost<WebSession>('password', { email, password });
  setSession(session);
  return session;
}

/** The authenticator step after a password. */
export async function completeMfa(mfaToken: string, code: string): Promise<WebSession> {
  const session = await sessionPost<WebSession>('mfa', { mfaToken, code });
  setSession(session);
  return session;
}

/** Ends the session on the server and forgets it here, whatever the server said. */
export async function signOut(): Promise<void> {
  try {
    await sessionPost('logout');
  } finally {
    setSession(undefined);
  }
}

// -- Calling the rest of the API as the signed-in person ---------------------

const DEVICE_KEY = 'jt-device';

/**
 * This browser's device handle, for the guest basket and guest orders.
 *
 * A random handle, not a secret, exactly as the Android app keeps one. It is
 * the only thing this module stores, and it is stored because a guest basket
 * has to survive a reload.
 */
export function deviceHandle(): string | undefined {
  try {
    const existing = window.localStorage.getItem(DEVICE_KEY);
    if (existing !== null && /^[A-Za-z0-9_-]{4,120}$/.test(existing)) return existing;
    const bytes = new Uint8Array(18);
    crypto.getRandomValues(bytes);
    const handle = `web_${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
    window.localStorage.setItem(DEVICE_KEY, handle);
    return handle;
  } catch {
    // Storage blocked (private mode, a strict browser): no handle, no guest basket.
    return undefined;
  }
}

function isFresh(session: WebSession): boolean {
  return new Date(session.accessTokenExpiresAt).getTime() - Date.now() > EXPIRY_MARGIN_MS;
}

/**
 * `fetch` against the API as the signed-in person.
 *
 * Adds the bearer token (refreshing first when it is about to expire), the
 * device handle, and retries once after a refresh if the server says the token
 * is no longer good. Without a session it calls the API anonymously; the route
 * decides whether that is allowed.
 */
export interface ApiFetchOptions {
  /** Default true. False for a route whose 401 may mean "wrong password", not "token refused". */
  readonly retryUnauthorized?: boolean;
  /**
   * Extra request headers for `apiSend` — an `idempotency-key`, say. They cannot
   * replace the bearer or the device handle, which `apiFetch` always sets last.
   */
  readonly headers?: Readonly<Record<string, string>>;
}

export async function apiFetch(
  path: string,
  init: RequestInit = {},
  options: ApiFetchOptions = {},
): Promise<Response> {
  let session = current;
  if (session !== undefined && !isFresh(session)) session = await refresh();

  const send = (token: string | undefined): Promise<Response> => {
    const headers = new Headers(init.headers);
    headers.set('accept', 'application/json');
    if (token !== undefined) headers.set('authorization', `Bearer ${token}`);
    const device = deviceHandle();
    if (device !== undefined) headers.set('x-jt-device', device);
    return fetch(`${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`, { ...init, headers });
  };

  const response = await send(session?.accessToken);
  if (response.status !== 401 || session === undefined) return response;

  // A route whose 401 can be an answer about something the person typed (a
  // wrong current password) is not retried: the server uses the same code for
  // a refused token, and retrying would send a wrong password twice, burning
  // the account's attempt limit and rotating the session for nothing.
  if (options.retryUnauthorized === false) return response;

  // The token was refused (expired early, or the session was ended elsewhere).
  const renewed = await refresh();
  return renewed === undefined ? response : send(renewed.accessToken);
}

/** `apiFetch` for a JSON envelope. Throws SessionApiError on a non-2xx. */
export async function apiGet<T>(path: string): Promise<T> {
  const response = await apiFetch(path);
  const payload = (await response.json().catch(() => undefined)) as
    { data?: T; error?: { message?: string; code?: string } } | undefined;
  if (!response.ok) {
    throw new SessionApiError(
      payload?.error?.message ?? 'Something went wrong.',
      response.status,
      payload?.error?.code,
    );
  }
  return payload?.data as T;
}

/**
 * A write to the API as the signed-in person, for a JSON envelope.
 *
 * The same bearer, device handle and refresh-on-401 as `apiFetch`. Throws
 * SessionApiError on a non-2xx, carrying the server's own plain message, so a
 * page can show exactly why something was refused.
 */
export async function apiSend<T>(
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
  options: ApiFetchOptions = {},
): Promise<T> {
  let response: Response;
  try {
    response = await apiFetch(
      path,
      {
        method,
        headers: {
          ...options.headers,
          ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        },
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      },
      options,
    );
  } catch {
    throw new SessionApiError(
      'We could not reach Just Treated. Check your connection and try again.',
      0,
      undefined,
    );
  }
  const payload = (await response.json().catch(() => undefined)) as
    { data?: T; error?: { message?: string; code?: string; details?: Record<string, unknown> } } | undefined;
  if (!response.ok) {
    throw new SessionApiError(
      payload?.error?.message ?? 'Something went wrong. Try again.',
      response.status,
      payload?.error?.code,
      payload?.error?.details ?? {},
    );
  }
  return payload?.data as T;
}

/** Test seam: forget everything this module holds. */
export function resetSessionForTests(): void {
  current = undefined;
  inflight = undefined;
  listeners.clear();
}
